// src/pages/Marketplace/surge-pricing/CurrentPriceCard.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  BASE_PRICE_CENTS,
  SURGE_DAY_OF_WEEK,
  formatInMarketTz,
  isoWeekdayInTz,
  useSurgeEvents,
} from '@/pages/Marketplace/shared';
import type { Market, PricingSource, SurgeEvent } from '@/pages/Marketplace/shared';

interface Props { market: Market; apiUrl: string; accessToken: string | null; }

interface Computed {
  priceCents: number;
  multiplier: number;
  source: PricingSource;
  winningEvent: SurgeEvent | null;
  dayMultiplier: number;
}

function compute(market: Market, events: SurgeEvent[], now: Date): Computed {
  const dayIdx = isoWeekdayInTz(now, market.timezone);
  const dayMultiplier = SURGE_DAY_OF_WEEK[dayIdx];
  const active = events.filter((e) => {
    if (e.deleted_at) return false;
    const start = new Date(e.starts_at).getTime();
    const end = new Date(e.ends_at).getTime();
    return now.getTime() >= start && now.getTime() < end;
  });
  const winner = active.reduce<SurgeEvent | null>((best, e) =>
    !best || e.multiplier > best.multiplier ? e : best, null);
  const multiplier = winner ? winner.multiplier : dayMultiplier;
  return {
    multiplier,
    priceCents: Math.round(BASE_PRICE_CENTS * multiplier),
    source: winner ? 'event_override' : 'day_of_week',
    winningEvent: winner,
    dayMultiplier,
  };
}

export default function CurrentPriceCard({ market, apiUrl, accessToken }: Props): React.ReactElement {
  const { data: events } = useSurgeEvents(apiUrl, accessToken, market.id, false);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  const computed = useMemo(
    () => compute(market, events ?? [], new Date()),
    // tick forces recompute every 30s; the dependency lint will flag events array identity — refetch covers the rest.
    [market, events, tick],
  );

  return (
    <div className="border border-violet-800/40 bg-gradient-to-r from-violet-950/40 to-pink-950/30 rounded-2xl p-6">
      <div className="text-sm text-gray-400">Current price — {market.name}</div>
      <div className="mt-2 text-4xl font-semibold text-white">
        ${(computed.priceCents / 100).toFixed(2)}
      </div>
      <div className="mt-1 text-sm">
        <span className="text-violet-300 font-medium">{computed.multiplier}×</span>{' '}
        <span className="text-gray-400">
          {computed.source === 'day_of_week'
            ? `(day-of-week · ${computed.dayMultiplier}× base)`
            : `(event: ${computed.winningEvent?.name}, ends ${
                computed.winningEvent ? formatInMarketTz(computed.winningEvent.ends_at, market.timezone, 'PP p') : ''
              })`}
        </span>
      </div>
      <div className="mt-3 text-xs text-gray-500">
        Live price in {market.timezone}. Updates every 30 seconds.
      </div>
    </div>
  );
}
